import { UserInfoWithIdT } from 'business/definitions';
import { useCallback, useEffect, useRef, useState } from 'react';

type AutoSaveOptions = {
    onSave: (
        html: string,
        users?: UserInfoWithIdT[],
        options?: { isAutoSave?: boolean },
    ) => void;
    users: UserInfoWithIdT[] | undefined;
    autoSaveDelay: number;
    content: string;
};

export function useAutoSave({
    onSave,
    users,
    autoSaveDelay,
    content,
}: AutoSaveOptions) {
    const [savingStatus, setSavingStatus] = useState<'saving' | 'saved' | null>(
        null,
    );
    const savedIndicatorTimeoutRef = useRef<number | null>(null);
    const initialContentRef = useRef<string>(content);
    const timeoutRef = useRef<number | null>(null);

    const cancelAutoSave = useCallback(() => {
        if (timeoutRef.current) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const debouncedSave = useCallback(
        (html: string) => {
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
            }

            if (html === initialContentRef.current) {
                return;
            }

            timeoutRef.current = window.setTimeout(() => {
                setSavingStatus('saving');

                if (users) {
                    onSave(html, users, { isAutoSave: true });
                } else {
                    onSave(html, undefined, { isAutoSave: true });
                }

                initialContentRef.current = html;
                setSavingStatus('saved');

                if (savedIndicatorTimeoutRef.current) {
                    window.clearTimeout(savedIndicatorTimeoutRef.current);
                }
                savedIndicatorTimeoutRef.current = window.setTimeout(() => {
                    setSavingStatus(null);
                }, 3000);
            }, autoSaveDelay);
        },
        [onSave, users, autoSaveDelay],
    );

    useEffect(() => {
        initialContentRef.current = content;
    }, [content]);

    useEffect(() => {
        return () => {
            cancelAutoSave();
            if (savedIndicatorTimeoutRef.current) {
                window.clearTimeout(savedIndicatorTimeoutRef.current);
            }
        };
    }, [cancelAutoSave]);

    return {
        savingStatus,
        cancelAutoSave,
        debouncedSave,
    };
}
